import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { WHATSAPP, buildWhatsappLink } from "@/lib/config";

const LeadForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_location: "",
    message: "",
  });

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Por favor, preencha ao menos nome e telefone.");
      return;
    }
    const loc = form.preferred_location || "Online";
    const phoneTarget =
      loc === "Mogi Guaçu"
        ? WHATSAPP.mogi_guacu
        : loc === "Artur Nogueira"
        ? WHATSAPP.artur_nogueira
        : WHATSAPP.online;

    const text =
      `Olá Dra. Mariana! Gostaria de agendar uma consulta.%0A%0A` +
      `*Nome:* ${form.name}%0A` +
      `*E-mail:* ${form.email || "-"}%0A` +
      `*WhatsApp:* ${form.phone}%0A` +
      `*Atendimento:* ${loc}%0A` +
      (form.message ? `*Mensagem:* ${form.message}` : "");

    window.open(`${buildWhatsappLink(phoneTarget).split("?")[0]}?text=${text}`, "_blank");
    toast.success("Abrindo o WhatsApp...");
  };

  return (
    <section
      id="contato"
      className="bg-[#FDFBF7] py-20 md:py-32"
      data-testid="lead-section"
    >
      <div className="mx-auto max-w-5xl px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-1 gap-12 rounded-[2.5rem] bg-gradient-to-br from-[#F5EFE6] to-[#FDFBF7] p-8 md:p-14 lg:grid-cols-12 lg:gap-16"
        >
          <div className="lg:col-span-5">
            <p className="mb-3 text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
              Primeiro passo
            </p>
            <h2 className="font-serif text-3xl leading-tight text-[#3E2723] sm:text-4xl" data-testid="lead-title">
              Deixe seus dados <br />
              <em className="italic text-[#8B6F1F]">e fale comigo pelo WhatsApp.</em>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#4E342E]/80">
              Preencha o formulário e você será direcionada ao meu WhatsApp
              com sua mensagem pronta para eu entender seu momento e agendar
              sua avaliação.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 lg:col-span-7" data-testid="lead-form">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs uppercase tracking-widest text-[#795548]">Nome completo</label>
                <Input value={form.name} onChange={handleChange("name")} placeholder="Seu nome" className="rounded-full border-[#EFEBE1] bg-white px-5 py-6 text-[#3E2723] focus-visible:ring-[#D4AF37]" data-testid="lead-input-name" required />
              </div>
              <div>
                <label className="mb-2 block text-xs uppercase tracking-widest text-[#795548]">E-mail</label>
                <Input type="email" value={form.email} onChange={handleChange("email")} placeholder="email@exemplo.com" className="rounded-full border-[#EFEBE1] bg-white px-5 py-6 text-[#3E2723] focus-visible:ring-[#D4AF37]" data-testid="lead-input-email" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs uppercase tracking-widest text-[#795548]">WhatsApp</label>
                <Input value={form.phone} onChange={handleChange("phone")} placeholder="(19) 99999-9999" className="rounded-full border-[#EFEBE1] bg-white px-5 py-6 text-[#3E2723] focus-visible:ring-[#D4AF37]" data-testid="lead-input-phone" required />
              </div>
              <div>
                <label className="mb-2 block text-xs uppercase tracking-widest text-[#795548]">Onde quer ser atendida?</label>
                <Select value={form.preferred_location} onValueChange={(v) => setForm((prev) => ({ ...prev, preferred_location: v }))}>
                  <SelectTrigger className="rounded-full border-[#EFEBE1] bg-white px-5 py-6 text-[#3E2723] focus:ring-[#D4AF37]" data-testid="lead-select-location">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Mogi Guaçu">Mogi Guaçu</SelectItem>
                    <SelectItem value="Artur Nogueira">Artur Nogueira</SelectItem>
                    <SelectItem value="Online">Online</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="mb-2 block text-xs uppercase tracking-widest text-[#795548]">Mensagem (opcional)</label>
              <Textarea value={form.message} onChange={handleChange("message")} placeholder="Conte um pouco sobre seu momento ou objetivo." rows={4} className="rounded-2xl border-[#EFEBE1] bg-white px-5 py-4 text-[#3E2723] focus-visible:ring-[#D4AF37]" data-testid="lead-input-message" />
            </div>
            <Button type="submit" className="w-full rounded-full bg-[#3E2723] py-6 text-xs uppercase tracking-[0.25em] text-white hover:bg-[#5D4037]" data-testid="lead-submit">
              <Send className="mr-2 h-4 w-4" />
              Enviar pelo WhatsApp
            </Button>
            <p className="text-center text-xs text-[#795548]">
              Ao enviar, você será direcionada ao WhatsApp com os dados preenchidos.
            </p>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default LeadForm;
